export interface LiveVoiceEventDeps {
  tool(call: {
    callId: string;
    name: string;
    arguments: Record<string, unknown>;
  }): Promise<unknown>;
  usage(responseId: string, usage: unknown): Promise<void>;
  send(event: unknown): void;
  caption(text: string): void;
  approval?(): void;
  phase(phase: "listening" | "thinking" | "speaking"): void;
}

/** One handler per call: duplicate provider events cannot execute an action twice. */
export function createLiveVoiceEventHandler(deps: LiveVoiceEventDeps) {
  const calls = new Set<string>();
  const responses = new Set<string>();
  const pendingTools = new Map<string, Promise<void>[]>();
  return async (event: Record<string, unknown>) => {
    if (event.type === "response.function_call_arguments.done") {
      if (
        typeof event.call_id !== "string" ||
        typeof event.name !== "string" ||
        typeof event.arguments !== "string" ||
        calls.has(event.call_id)
      )
        return;
      const callId = event.call_id;
      const name = event.name;
      const argumentsJson = event.arguments;
      calls.add(callId);
      const responseId = typeof event.response_id === "string" ? event.response_id : "";
      const tools = pendingTools.get(responseId) ?? [];
      pendingTools.set(responseId, tools);
      const work = (async () => {
        let result: unknown;
        try {
          const args: unknown = JSON.parse(argumentsJson);
          if (!args || typeof args !== "object" || Array.isArray(args)) throw new Error();
          result = await deps.tool({
            callId,
            name,
            arguments: args as Record<string, unknown>,
          });
        } catch {
          result = { error: "Wafi couldn't complete that action. Check the chat." };
        }
        if (
          result &&
          typeof result === "object" &&
          "status" in result &&
          result.status === "pending approval"
        )
          deps.approval?.();
        deps.send({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify(result ?? null),
          },
        });
      })();
      tools.push(work);
      await work;
    } else if (event.type === "response.done") {
      const response = event.response as { id?: string; usage?: unknown } | undefined;
      if (response?.id && responses.has(response.id)) return;
      if (response?.id) responses.add(response.id);
      const key = response?.id && pendingTools.has(response.id) ? response.id : "";
      const tools = pendingTools.get(key) ?? [];
      pendingTools.delete(key);
      if (response?.id && response.usage) await deps.usage(response.id, response.usage);
      await Promise.all(tools);
      if (tools.length) deps.send({ type: "response.create" });
      deps.phase("listening");
    } else if (event.type === "input_audio_buffer.speech_started") {
      deps.phase("listening");
    } else if (event.type === "input_audio_buffer.speech_stopped") {
      deps.phase("thinking");
    } else if (
      event.type === "response.output_audio_transcript.delta" &&
      typeof event.delta === "string"
    ) {
      deps.phase("speaking");
      deps.caption(event.delta);
    } else if (event.type === "response.created") {
      deps.caption("");
    } else if (event.type === "error") {
      throw new Error("Wafi couldn't connect. Try again.");
    }
  };
}
